import { useEffect, useState } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Products", href: "#products" },
  { label: "Grades", href: "#grades" },
  { label: "Calculator", href: "#calculator" },
  { label: "Clients", href: "#clients" },
  { label: "About", href: "#about" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const LogoMark = () => (
  <img
    src="/rew-logo.png"
    alt="Rahil Engineering Works"
    width="54"
    height="30"
    className="h-14 sm:h-16 w-auto shrink-0 transition-transform duration-500 group-hover:scale-105"
  />
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    const onScroll = () => setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-[100]">
      <motion.div
        className="h-[3px] bg-brand origin-left"
        style={{ scaleX: progress }}
        data-testid="scroll-progress"
      />
      <div className="bg-white/92 backdrop-blur-md border-b border-steel-light">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[92px] flex items-center justify-between">
          <a href="#top" className="group flex items-center gap-3" data-testid="nav-logo-link">
            <LogoMark />
            <span className="leading-none border-l border-steel-light pl-3 sm:pl-4">
              <span className="block font-display font-black text-xl sm:text-[26px] tracking-wide uppercase text-ink leading-none">
                Rahil
              </span>
              <span className="block font-sans font-semibold text-[11px] sm:text-xs tracking-[0.12em] uppercase text-navy/70 mt-1">
                Engineering Works
              </span>
            </span>
          </a>

          <nav className="hidden xl:flex items-center gap-6">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-sans font-medium text-[11px] uppercase tracking-[0.12em] text-navy hover:text-brand transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-quote-btn"
              className="font-sans font-medium text-[11px] uppercase tracking-[0.14em] font-bold bg-brand text-white px-6 py-3 rounded-md hover:bg-ink transition-colors duration-300"
            >
              Get a Quote
            </a>
          </nav>

          <button
            className="xl:hidden p-2 text-ink"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="xl:hidden overflow-hidden border-t border-steel-light bg-white"
              data-testid="nav-mobile-menu"
            >
              <div className="px-5 py-4 flex flex-col gap-4">
                {LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="font-sans font-medium text-xs uppercase tracking-[0.14em] text-navy"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href="#contact"
                  data-testid="nav-mobile-quote-btn"
                  className="font-sans font-medium text-xs uppercase tracking-[0.14em] font-bold bg-brand text-ink px-5 py-3 text-center"
                >
                  Get a Quote
                </a>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
